import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Popover from '@radix-ui/react-popover'
import { ChevronDown, ChevronUp, Plus, UserPlus, Upload, CheckCircle2, FileText, Link } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type {
  Requirement,
  RequirementType,
  RequirementStatus,
  Recommender,
  RecommendationStatus,
  Document,
} from '@/types/models'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REQUIREMENT_TYPES: RequirementType[] = [
  'ESSAY', 'LOR', 'TEST_SCORE', 'TRANSCRIPT', 'RESUME', 'INTERVIEW', 'FINANCIAL', 'OTHER',
]

const STATUS_CYCLE: RequirementStatus[] = ['TODO', 'IN_PROGRESS', 'COMPLETED']

const STATUS_LABELS: Record<RequirementStatus, string> = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
}

const STATUS_COLORS: Record<RequirementStatus, string> = {
  TODO: 'bg-muted text-muted-foreground',
  IN_PROGRESS: 'bg-brand-blue/10 text-brand-blue',
  COMPLETED: 'bg-brand-green/10 text-brand-green',
}

const RECOMMENDATION_STATUS_LABELS: Record<RecommendationStatus, string> = {
  NOT_REQUESTED: 'Not Requested',
  REQUESTED: 'Requested',
  AGREED: 'Agreed',
  IN_PROGRESS: 'In Progress',
  SUBMITTED: 'Submitted',
  RECEIVED: 'Received',
}

const RECOMMENDATION_STATUS_COLORS: Record<RecommendationStatus, string> = {
  NOT_REQUESTED: 'bg-muted text-muted-foreground',
  REQUESTED: 'bg-yellow-100 text-yellow-800',
  AGREED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-brand-blue/10 text-brand-blue',
  SUBMITTED: 'bg-purple-100 text-purple-800',
  RECEIVED: 'bg-brand-green/10 text-brand-green',
}

// Types that support document attachment (upload new or link existing)
const UPLOADABLE_TYPES = new Set<RequirementType>(['ESSAY', 'LOR', 'TEST_SCORE', 'RESUME', 'TRANSCRIPT'])

// ---------------------------------------------------------------------------
// Sub-component: DocumentPickerPopover
// Lets the user link an existing workspace document to a requirement.
// ---------------------------------------------------------------------------

interface DocumentPickerPopoverProps {
  requirementId: string
  applicationId: string
}

function DocumentPickerPopover({ requirementId, applicationId }: DocumentPickerPopoverProps) {
  const [open, setOpen] = useState(false)
  const [linkError, setLinkError] = useState('')
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspaceStore()

  const { data: docsData, isLoading } = useQuery<ApiResponse<Document[]>>({
    queryKey: ['documents', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<Document[]>>('/v1/documents'),
    enabled: open && !!currentWorkspaceId,
  })

  const linkMutation = useMutation({
    mutationFn: (documentId: string) =>
      api.post(`/v1/documents/${documentId}/link`, { requirementId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      setLinkError('')
      setOpen(false)
    },
    onError: (err: Error) => {
      setLinkError(err.message || 'Failed to link document.')
    },
  })

  const docs = docsData?.data ?? []

  return (
    <Popover.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLinkError('') }}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <Link className="h-3 w-3" />
          Link Existing
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          className="z-50 w-64 max-h-56 overflow-y-auto rounded-lg border bg-popover shadow-lg"
        >
          {isLoading && (
            <p className="p-3 text-xs text-muted-foreground">Loading...</p>
          )}
          {!isLoading && docs.length === 0 && (
            <p className="p-3 text-xs text-muted-foreground">No documents in your library yet.</p>
          )}
          {docs.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => linkMutation.mutate(doc.id)}
              disabled={linkMutation.isPending}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent disabled:opacity-50"
            >
              <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-foreground truncate">{doc.name}</p>
                <p className="text-xs text-muted-foreground">{doc.type}</p>
              </div>
            </button>
          ))}
          {linkError && (
            <p className="px-3 pb-2 text-xs text-destructive">{linkError}</p>
          )}
          <Popover.Arrow className="fill-border" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

// ---------------------------------------------------------------------------
// Sub-component: TestScoreRow
// Renders for TEST_SCORE requirements that have metadata.testCategory set.
// Lets the user pick which test they're submitting and upload a score report.
// ---------------------------------------------------------------------------

interface TestScoreMeta {
  testCategory: 'aptitude' | 'language'
  acceptedTests: string[]
  selectedTest?: string
  scoreReportDocumentId?: string
  scoreReportName?: string
}

interface TestScoreRowProps {
  requirement: Requirement
  applicationId: string
}

function TestScoreRow({ requirement, applicationId }: TestScoreRowProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const meta = (requirement.metadata ?? {}) as unknown as TestScoreMeta
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const patchMutation = useMutation({
    mutationFn: (data: Partial<Requirement>) =>
      api.patch(`/v1/requirements/${requirement.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
  })

  function handleTestSelect(test: string) {
    patchMutation.mutate({
      metadata: { ...meta, selectedTest: test },
      status: requirement.status === 'TODO' ? 'IN_PROGRESS' : requirement.status,
    })
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadError('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', file.name)
      formData.append('type', 'TEST_SCORE')
      formData.append('requirementId', requirement.id)
      formData.append('applicationId', applicationId)
      const res = await api.upload<{ success: boolean; data: { id: string; name: string } }>(
        '/v1/documents/upload',
        formData,
      )
      if (res.success) {
        patchMutation.mutate({
          metadata: { ...meta, scoreReportDocumentId: res.data.id, scoreReportName: res.data.name },
          status: meta.selectedTest ? 'COMPLETED' : requirement.status,
        })
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const isComplete = requirement.status === 'COMPLETED'

  return (
    <div className={cn('rounded-md border bg-card', isComplete && 'opacity-75')}>
      <div className="flex items-start gap-3 p-3">
        {/* Status indicator */}
        <div className="mt-0.5 flex-shrink-0">
          {isComplete ? (
            <CheckCircle2 className="h-5 w-5 text-brand-green" />
          ) : (
            <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/40" />
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('text-sm font-medium', isComplete && 'line-through text-muted-foreground')}>
              {requirement.title}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {meta.testCategory === 'aptitude' ? 'Aptitude' : 'Language'}
            </span>
            {isComplete && (
              <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-xs font-medium text-brand-green">
                Completed
              </span>
            )}
          </div>

          {/* Test picker */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Test:</span>
            <select
              value={meta.selectedTest ?? ''}
              onChange={(e) => handleTestSelect(e.target.value)}
              disabled={patchMutation.isPending}
              className="rounded border bg-background px-2 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <option value="" disabled>Select test…</option>
              {meta.acceptedTests?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Score report upload */}
          <div className="flex flex-wrap items-center gap-2">
            {meta.scoreReportName ? (
              <span className="text-xs text-brand-green">
                ✓ {meta.scoreReportName}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || patchMutation.isPending}
              className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              <Upload className="h-3 w-3" />
              {uploading ? 'Uploading…' : meta.scoreReportName ? 'Replace Report' : 'Upload Score Report'}
            </button>
            <DocumentPickerPopover requirementId={requirement.id} applicationId={applicationId} />
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
          {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-component: RequirementRow
// ---------------------------------------------------------------------------

interface RequirementRowProps {
  requirement: Requirement
  applicationId: string
}

const REQUIREMENT_TYPE_TO_DOC_TYPE: Partial<Record<RequirementType, string>> = {
  ESSAY: 'ESSAY',
  RESUME: 'RESUME',
  TRANSCRIPT: 'TRANSCRIPT',
  LOR: 'RECOMMENDATION_LETTER',
  TEST_SCORE: 'TEST_SCORE',
}

function RequirementRow({ requirement, applicationId }: RequirementRowProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignError, setAssignError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const canUpload = UPLOADABLE_TYPES.has(requirement.type)

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadError('')
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('name', file.name)
      formData.append('type', REQUIREMENT_TYPE_TO_DOC_TYPE[requirement.type] ?? 'OTHER')
      formData.append('requirementId', requirement.id)
      formData.append('applicationId', applicationId)
      await api.upload('/v1/documents/upload', formData)
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const patchMutation = useMutation({
    mutationFn: (data: Partial<Requirement>) =>
      api.patch(`/v1/requirements/${requirement.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
  })

  function cycleStatus() {
    const idx = STATUS_CYCLE.indexOf(requirement.status)
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]
    patchMutation.mutate({ status: next })
  }

  const { data: recommendersData } = useQuery<ApiResponse<Recommender[]>>({
    queryKey: ['recommenders'],
    queryFn: () => api.get<ApiResponse<Recommender[]>>('/v1/recommenders'),
    enabled: requirement.type === 'LOR' && assignOpen,
  })

  const assignMutation = useMutation({
    mutationFn: (recommenderId: string) =>
      api.post('/v1/recommendation-requests', {
        recommenderId,
        requirementId: requirement.id,
        applicationId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      setAssignError('')
      setAssignOpen(false)
    },
    onError: (err: Error) => {
      setAssignError(err.message || 'Failed to assign recommender.')
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
    },
  })

  const resendMutation = useMutation({
    mutationFn: () =>
      api.post(`/v1/recommendation-requests/${requirement.recommendationRequestId}/resend`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
    },
  })

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-center gap-3 p-3">
        {/* Status checkbox */}
        <button
          type="button"
          onClick={cycleStatus}
          disabled={patchMutation.isPending}
          aria-label={`Status: ${STATUS_LABELS[requirement.status]}. Click to advance.`}
          className={cn(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            requirement.status === 'COMPLETED'
              ? 'border-brand-green bg-brand-green text-white'
              : requirement.status === 'IN_PROGRESS'
              ? 'border-brand-blue bg-brand-blue/10'
              : 'border-muted-foreground/40',
          )}
        >
          {requirement.status === 'COMPLETED' && (
            <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {requirement.status === 'IN_PROGRESS' && (
            <span className="h-2 w-2 rounded-full bg-brand-blue" />
          )}
        </button>

        {/* Title + badges */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'text-sm font-medium',
                requirement.status === 'COMPLETED' && 'line-through text-muted-foreground',
              )}
            >
              {requirement.title}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {requirement.type}
            </span>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_COLORS[requirement.status],
              )}
            >
              {STATUS_LABELS[requirement.status]}
            </span>
          </div>
          {requirement.dueDate && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Due: {new Date(requirement.dueDate).toLocaleDateString()}
            </p>
          )}

          {/* LOR recommender info */}
          {requirement.type === 'LOR' && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {requirement.recommenderName ? (
                <span className="text-xs text-muted-foreground">
                  {requirement.recommenderName}
                </span>
              ) : null}
              {requirement.recommendationStatus && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    RECOMMENDATION_STATUS_COLORS[requirement.recommendationStatus],
                  )}
                >
                  {RECOMMENDATION_STATUS_LABELS[requirement.recommendationStatus]}
                </span>
              )}
              {requirement.recommendationRequestId && requirement.recommendationStatus !== 'SUBMITTED' && (
                <button
                  type="button"
                  onClick={() => resendMutation.mutate()}
                  disabled={resendMutation.isPending}
                  className="flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                >
                  {resendMutation.isPending ? 'Sending...' : resendMutation.isSuccess ? 'Sent!' : 'Resend'}
                </button>
              )}
              <Popover.Root open={assignOpen} onOpenChange={(o) => { setAssignOpen(o); if (!o) setAssignError('') }}>
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded text-xs text-brand-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <UserPlus className="h-3 w-3" />
                    {requirement.recommenderName ? 'Reassign' : 'Assign Recommender'}
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50 w-64 rounded-lg border bg-popover p-2 shadow-lg"
                  >
                    <p className="mb-2 text-xs font-semibold text-muted-foreground">
                      Select recommender
                    </p>
                    {recommendersData?.data?.length === 0 && (
                      <p className="text-xs text-muted-foreground">No recommenders added yet.</p>
                    )}
                    {recommendersData?.data?.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => assignMutation.mutate(r.id)}
                        disabled={assignMutation.isPending}
                        className="flex w-full flex-col rounded-md px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50"
                      >
                        <span className="font-medium">{r.name}</span>
                        <span className="text-xs text-muted-foreground">{r.institution}</span>
                      </button>
                    ))}
                    {assignError && (
                      <p className="mt-1 text-xs text-destructive">{assignError}</p>
                    )}
                    <Popover.Arrow className="fill-border" />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </div>
          )}
        </div>

        {/* Expand toggle */}
        {(requirement.description || canUpload) && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Expanded section — description + upload */}
      {expanded && (
        <div className="border-t px-3 pb-3 pt-2 space-y-2">
          {requirement.description && (
            <p className="text-sm text-muted-foreground">{requirement.description}</p>
          )}
          {canUpload && (
            <div className="space-y-1.5">
              {requirement.documents && requirement.documents.length > 0 && (
                <div className="space-y-1">
                  {requirement.documents.map((doc) => (
                    <p key={doc.id} className="text-xs text-brand-green">✓ {doc.name}</p>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                >
                  <Upload className="h-3 w-3" />
                  {uploading ? 'Uploading…' : 'Upload File'}
                </button>
                <DocumentPickerPopover requirementId={requirement.id} applicationId={applicationId} />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileUpload}
              />
              {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-component: AddRequirementForm
// ---------------------------------------------------------------------------

interface AddRequirementFormProps {
  applicationId: string
  onClose: () => void
}

function AddRequirementForm({ applicationId, onClose }: AddRequirementFormProps) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [type, setType] = useState<RequirementType>('ESSAY')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post(`/v1/requirements`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', applicationId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) {
      setError('Title is required.')
      return
    }
    setError('')
    createMutation.mutate({
      applicationId,
      title: title.trim(),
      type,
      description: description.trim() || undefined,
      dueDate: dueDate || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 rounded-md border bg-card p-3 space-y-3">
      <p className="text-xs font-semibold text-muted-foreground">Add Custom Requirement</p>
      <div>
        <input
          type="text"
          placeholder="Title *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as RequirementType)}
          className="flex-1 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {REQUIREMENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="flex-1 rounded border bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <textarea
        placeholder="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        className="w-full rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
        >
          {createMutation.isPending ? 'Adding...' : 'Add'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border px-3 py-1.5 text-xs hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// RequirementsPanel
// ---------------------------------------------------------------------------

interface Props {
  applicationId: string
}

/**
 * RequirementsPanel — renders inside ApplicationDrawer Requirements tab.
 *
 * Groups requirements by type. Supports inline status cycling, LOR recommender
 * assignment, and adding custom requirements.
 */
export default function RequirementsPanel({ applicationId }: Props) {
  const [showAddForm, setShowAddForm] = useState(false)

  const { data, isLoading } = useQuery<ApiResponse<Requirement[]>>({
    queryKey: ['requirements', applicationId],
    queryFn: () => api.get<ApiResponse<Requirement[]>>(`/v1/requirements?applicationId=${applicationId}`),
    enabled: !!applicationId,
  })

  const requirements = data?.data ?? []

  // Group by type
  const grouped = REQUIREMENT_TYPES.reduce<Record<RequirementType, Requirement[]>>(
    (acc, type) => {
      acc[type] = requirements.filter((r) => r.type === type)
      return acc
    },
    {} as Record<RequirementType, Requirement[]>,
  )

  if (isLoading) {
    return <div className="py-6 text-center text-sm text-muted-foreground">Loading...</div>
  }

  return (
    <div className="space-y-4">
      {REQUIREMENT_TYPES.map((type) => {
        const items = grouped[type]
        if (items.length === 0) return null
        return (
          <div key={type}>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {type.replace('_', ' ')}
            </h4>
            <div className="space-y-2">
              {items.map((req) => {
                const meta = req.metadata as Record<string, unknown> | null
                if (req.type === 'TEST_SCORE' && meta?.testCategory) {
                  return (
                    <TestScoreRow
                      key={req.id}
                      requirement={req}
                      applicationId={applicationId}
                    />
                  )
                }
                return (
                  <RequirementRow
                    key={req.id}
                    requirement={req}
                    applicationId={applicationId}
                  />
                )
              })}
            </div>
          </div>
        )
      })}

      {requirements.length === 0 && (
        <p className="text-sm text-muted-foreground">No requirements yet. Add one below.</p>
      )}

      {/* Add requirement */}
      {showAddForm ? (
        <AddRequirementForm
          applicationId={applicationId}
          onClose={() => setShowAddForm(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4" />
          Add Custom Requirement
        </button>
      )}
    </div>
  )
}
